import { Store, MutationPayload } from "vuex";
import {
  documentStore,
  globalStore,
  snapshotStore,
  uiStore
} from "@/store/store";
import { getModuleNameFromMutation } from "@/store/util";
import { exportTree } from "../tree/tree";
import { getSnapshotTitleFromMutation } from "./snapshot-labels/utils";
import { PanelState } from "@/types/ui";

class BreakdanceUndo {
  modulesToWatch = [
    "document",
    "global"
  ]; /* modules must be namespaced: true; */

  store!: Store<any>;

  isPartOfExistingTransaction = false;

  transactionDepth = 0;
  isSubscribed = false;

  shouldAllMutationsBeIgnored = false;

  setupHook(store: Store<any>) {
    this.store = store;
  }

  initialize() {
    /*
    breakdanceUndo.initialize can be called multiple times...
    subscribing to the store should only happen once
    */
    if (!this.isSubscribed) {
      this.store.subscribe(
        this.mutationListener.bind(this)
      ); /* if we don't bind(this), this doesn't refer to the
      instance of this class inside mutationListener when called by Vuex...
      why the fuck not? */

      this.isSubscribed = true;
    }

    this.isPartOfExistingTransaction = false;

    snapshotStore.clear();
    this.takeSnapshot("Initial State");
  }

  /**
   * all mutations made after calling startTransaction will be bundled
   * into a single undo/redo snapshot when endTransaction is called
   * or will be discarded if cancelTransactionIfItIsStillInProgress is called (use cancelTransaction
   * when nothing happens i.e. a drag operation that doesn't do anything)
   * if you never call endTransaction or cancelTransaction shit will
   * be fucked sideways. so don't fuck up.
   *
   */
  startTransaction() {
    if (this.isPartOfExistingTransaction) {
      console.warn(
        "Can't start a transaction when a transaction is is already in progress."
      );
    }

    this.isPartOfExistingTransaction = true;
  }

  cancelTransactionIfItIsStillInProgress() {
    this.isPartOfExistingTransaction = false;
  }

  endTransaction(
    snapshotTitle: string,
    overwritePreviousTransactionWithKey: string | undefined = undefined
  ) {
    if (!this.isPartOfExistingTransaction) {
      console.trace("Can't end a transaction that is not in progress");
    }

    this.takeSnapshot(snapshotTitle, overwritePreviousTransactionWithKey);
    this.isPartOfExistingTransaction = false;
  }

  setStoreFromCurrentSnapshot() {
    const currentSnapshot = snapshotStore.currentSnapshot;

    if (currentSnapshot) {
      const panelStateWithOpenedHistoryPanelIfItsOpenedNow: PanelState = {
        ...currentSnapshot.panelState,
        rightSidebarPanel:
          uiStore.panelState.rightSidebarPanel === "history"
            ? "history"
            : currentSnapshot.panelState.rightSidebarPanel
      };

      if (
        panelStateWithOpenedHistoryPanelIfItsOpenedNow.rightSidebarPanel ===
          "globalSettings" ||
        panelStateWithOpenedHistoryPanelIfItsOpenedNow.rightSidebarPanel ===
          "selectors"
      ) {
        panelStateWithOpenedHistoryPanelIfItsOpenedNow.shouldLoadFreshDataIntoStoreOnceOpened[
          panelStateWithOpenedHistoryPanelIfItsOpenedNow.rightSidebarPanel
        ] = false;
      }

      if (
        panelStateWithOpenedHistoryPanelIfItsOpenedNow.leftSidebarPanel ===
          "globalSettings" ||
        panelStateWithOpenedHistoryPanelIfItsOpenedNow.leftSidebarPanel ===
          "selectors"
      ) {
        panelStateWithOpenedHistoryPanelIfItsOpenedNow.shouldLoadFreshDataIntoStoreOnceOpened[
          panelStateWithOpenedHistoryPanelIfItsOpenedNow.leftSidebarPanel
        ] = false;
      }
      breakdanceUndo.ignore(() => {
        if ("document" in currentSnapshot) {
          /* defensiveCopy isn't needed because currentSnapshot is already defensive copied */
          documentStore.setDocument(currentSnapshot.document);
        }

        globalStore.setGlobalSettings(currentSnapshot.globalSettings);
        globalStore.setSelectors(currentSnapshot.cssSelectors);
      });

      if ("activeElementId" in currentSnapshot) {
        uiStore.activateElementMutation(currentSnapshot.activeElementId);
      }

      uiStore.setActiveSelectorName(currentSnapshot.activeSelectorName);
      uiStore.setPanelState(panelStateWithOpenedHistoryPanelIfItsOpenedNow);
    }
  }

  redo() {
    if (snapshotStore.canRedo) {
      snapshotStore.redoIndex();
      this.setStoreFromCurrentSnapshot();
    }
  }

  undo() {
    if (snapshotStore.canUndo) {
      snapshotStore.undoIndex();
      this.setStoreFromCurrentSnapshot();
    }
  }

  takeSnapshot(
    title: string,
    overwritePreviousTransactionWithKey: string | undefined = undefined
  ) {
    if (uiStore.mode === "browse") {
      snapshotStore.takeBrowseModeSnapshot({
        globalSettings: { ...globalStore.globalSettings },
        cssSelectors: [...globalStore.cssSelectors],
        activeSelectorName: uiStore.activeSelectorName,
        panelState: { ...uiStore.panelState },
        key: overwritePreviousTransactionWithKey,
        title
      });
    } else {
      snapshotStore.takeBuilderSnapshot({
        document: {
          documentMeta: { ...documentStore.document.documentMeta },
          tree: {
            ...exportTree(documentStore.document.tree)
          }
        },
        globalSettings: { ...globalStore.globalSettings },
        cssSelectors: [...globalStore.cssSelectors],
        activeElementId: uiStore.activeElementId,
        activeSelectorName: uiStore.activeSelectorName,
        panelState: { ...uiStore.panelState },
        key: overwritePreviousTransactionWithKey,
        title
      });
    }
  }

  /**
   * bundle multiple mutations into a single transaction that can be undone/redone in one click
   */
  transaction(
    callbackThatRunsTheMutations: any,
    snapshotTitle: string,
    overwritePreviousTransactionWithKey: string | undefined = undefined
  ) {
    if (this.isPartOfExistingTransaction) {
      console.trace();
      console.error(
        "you called transaction() from inside another transaction(), or after startTransaction was called."
      );
    }

    this.isPartOfExistingTransaction = true;
    callbackThatRunsTheMutations();
    if (snapshotTitle) {
      this.takeSnapshot(snapshotTitle, overwritePreviousTransactionWithKey);
    }
    this.isPartOfExistingTransaction = false;
  }

  ignore(callbackThatRunsMutations: () => unknown) {
    if (this.isPartOfExistingTransaction) {
      console.trace();
      console.error("you called ignore() from inside transaction()");
    }

    if (this.shouldAllMutationsBeIgnored) {
      console.trace();
      console.error("you called ignore() from inside another ignore()");
    }

    this.shouldAllMutationsBeIgnored = true;

    callbackThatRunsMutations();

    this.shouldAllMutationsBeIgnored = false;
  }

  /**
   * This is a fallback for scenarios where either:
   * a) Store action haven't wrapped mutation call with undo transaction
   * b) Store action call haven't been wrapped with undo transaction
   */
  mutationListener(mutation: MutationPayload) {
    if (this.isPartOfExistingTransaction) {
      return;
    }

    if (this.shouldMutationBeIgnored(mutation)) {
      return;
    }

    this.takeSnapshot(getSnapshotTitleFromMutation(mutation));
  }

  shouldMutationBeIgnored(mutation: MutationPayload): boolean {
    if (this.shouldAllMutationsBeIgnored) {
      return true;
    }

    const moduleName = getModuleNameFromMutation(mutation);

    if (!this.modulesToWatch.includes(moduleName)) {
      return true;
    }

    return false;
  }
}

export const breakdanceUndo = new BreakdanceUndo();

export const vuexUndoPluginInitFunction = (store: Store<any>) => {
  breakdanceUndo.setupHook(store);
  // @ts-ignore
  window.breakdanceUndo = breakdanceUndo; // useful for debugging in the console, but not needed. can be deleted.
};
